import React, { useEffect } from 'react';
import { PhoneOff, PhoneMissed, MailCheck, MailWarning, CalendarCheck, CalendarX } from 'lucide-react';
import { useContact } from '../../context/ContactContext';
import OutboundDialer from './OutboundDialer';
import CallbacksPanel from './CallbacksPanel';
import '../../styles/idle-v3.css';

// Logged in, no active contact — the Voice tab's idle screen.
//
// Idle screen v3 (2026-09-19): the scheduled-callbacks list is shown here in
// full — the SAME CallbacksPanel the Task tab renders (same data, same "Call
// now" buttons), replacing the one-line strip that only linked to it. The
// Task tab is unchanged. No stats bar, because no real reporting feeds one.
// The wordmark lives in the left rail (2026-09-21, vault #187), not here —
// the rail is on every screen, so a header here would show it twice.
//
// v3 design parity (#115, epic #117): styled to the frozen spec's Available /
// outbound state through styles/idle-v3.css, every rule scoped to
// .idle-v3-screen so the Task tab and the callback-task card, which share
// OutboundDialer and CallbacksPanel, keep their own look. Markup only here:
// the scope class and the spec's pulse mark; behaviour and copy unchanged.
export default function WaitingForContact() {
  const { agentStatus, stuckContacts, callCancelledAt, clearCallCancelled, callMissedAt, clearCallMissed, wrapAlert, wrapCallback } = useContact();

  // D39 (2026-09-16): confirm a cancelled outbound call for a few seconds. The
  // console used to show a red softphone-fault banner here, which is what the
  // hang-up actually produces in Streams; this says what really happened.
  useEffect(() => {
    if (!callCancelledAt) return undefined;
    const t = setTimeout(() => clearCallCancelled(), 6000);
    return () => clearTimeout(t);
  }, [callCancelledAt, clearCallCancelled]);
  // 2026-09-23 (ring-out banner): an inbound ring that timed out unanswered is
  // reported as what it was. Held longer than the cancel notice: the agent
  // did not act, so there is nothing else on screen to tell them.
  useEffect(() => {
    if (!callMissedAt) return undefined;
    const t = setTimeout(() => clearCallMissed(), 12000);
    return () => clearTimeout(t);
  }, [callMissedAt, clearCallMissed]);
  const stuck = (stuckContacts?.length ?? 0) > 0;

  return (
    <main className="centre-panel idle-v3-screen">
      <div className="waiting-state idle-v3">
        <span className="waiting-icon" aria-hidden="true">
          <span className="waiting-pulse" />
        </span>
        <h2 className="waiting-title">Waiting for next contact</h2>
        {stuck ? (
          <p className="waiting-sub">
            You're signed in but <strong>not routable</strong> — a missed contact is
            holding your session. Clear it from the panel on the left.
          </p>
        ) : (
          <p className="waiting-sub">
            You're signed in{agentStatus ? ` · ${agentStatus}` : ''}. Customer
            details and AI Assist will appear here when a contact connects.
          </p>
        )}
        {callCancelledAt && (
          <p className="call-cancelled-notice" role="status">
            <PhoneOff size={14} />
            <span>Call cancelled before it was answered.</span>
          </p>
        )}
        {callMissedAt && (
          <p className="call-cancelled-notice call-missed-notice" role="status">
            <PhoneMissed size={14} />
            <span>Call missed — not answered here.</span>
          </p>
        )}
        {/* 2026-09-16: the client-alert outcome of the wrap-up just saved. The
            Lambda always returned it; until now the agent never saw whether
            the client was actually told. */}
        {wrapAlert && (
          <p className={`wrap-alert-notice ${wrapAlert.sent ? 'ok' : 'failed'}`} role="status">
            {wrapAlert.sent ? <MailCheck size={14} /> : <MailWarning size={14} />}
            <span>
              {wrapAlert.sent
                ? (wrapAlert.recipient === 'tenant'
                    ? "Client alert sent to the client's operations contact (knktr copied)."
                    : 'Client alert sent to knktr admin (this client has no operations contact on file yet).')
                : `Wrap-up saved, but the client alert could not be sent${wrapAlert.error ? ` — ${wrapAlert.error}` : ''}. Tell the client another way.`}
            </span>
          </p>
        )}
        {/* 2026-09-17: the agent-scheduled callback outcome of the wrap-up
            just saved (vault #133). The Lambda always returned it; until now
            the agent never saw whether the callback was actually booked. */}
        {wrapCallback && (
          <p className={`wrap-callback-notice ${wrapCallback.booked ? 'ok' : 'failed'}`} role="status">
            {wrapCallback.booked ? <CalendarCheck size={14} /> : <CalendarX size={14} />}
            <span>
              {wrapCallback.booked
                ? `Callback booked${wrapCallback.windowText ? ` — ${wrapCallback.windowText}` : ''}.`
                : `Wrap-up saved, but the callback could not be booked${wrapCallback.error ? ` — ${wrapCallback.error}` : ''}. Book it manually.`}
            </span>
          </p>
        )}
        <OutboundDialer />
        {/* Below the dialer: the list is the part that grows, so it never
            pushes the dialer out of view. */}
        <CallbacksPanel />
      </div>
    </main>
  );
}
